import {ExternalForecastComparison, ExternalForecastYear} from '../external-forecast.model';

// Test/dev data for the External Forecast comparison endpoint. `comparison.fixture.json` is the
// spec fixture and must stay equal to COMPARISON_FIXTURE (enforced by external-forecast.fixture.spec.ts).

export const FIXTURE_PLAN_ID = "6f1c2a9e-0b7d-4f7a-9a51-2b0f3d1e8c44";

type MonthTuple = [month: number, contactsReceived: number, partial?: boolean];

function buildYear(year: number, months: MonthTuple[]): ExternalForecastYear {
  return {
    year,
    total: months.reduce((sum, [, value]) => sum + value, 0),
    months: months.map(([month, contactsReceived, partial]) => ({
      month: `${year}-${String(month).padStart(2, "0")}`,
      contactsReceived,
      partial: !!partial
    }))
  };
}

function fullYear(year: number, base: number): ExternalForecastYear {
  return buildYear(year, Array.from({length: 12}, (_, i) => [i + 1, base + i * 1013] as MonthTuple));
}

function comparison(planId: string, planStartDate: string, planEndDate: string,
                    years: ExternalForecastYear[]): ExternalForecastComparison {
  return {planId, planStartDate, planEndDate, snapshotAt: "2026-10-12T14:03:22Z", years};
}

/** The spec §5.1 example extended to 12 months (2026-03-15 .. 2027-02-28), March partial. */
export const COMPARISON_FIXTURE: ExternalForecastComparison = comparison(
  FIXTURE_PLAN_ID, "2026-03-15", "2027-02-28", [
    buildYear(2026, [
      [3, 20114, true], [4, 40977], [5, 41820], [6, 39655], [7, 42310],
      [8, 43002], [9, 44118], [10, 45690], [11, 47209], [12, 47985]
    ]),
    buildYear(2027, [[1, 42110], [2, 38910]])
  ]);

export type ExternalForecastMockScenario =
  "ok" | "single-year" | "future" | "past" | "zeros" | "long";

/** 200-response variants used by unit tests; the dev mock interceptor serves "ok". */
export function comparisonScenario(scenario: ExternalForecastMockScenario, planId: string): ExternalForecastComparison {
  switch (scenario) {
    case "single-year":
      return comparison(planId, "2026-04-10", "2026-10-20", [
        buildYear(2026, [[4, 30550, true], [5, 41820], [6, 39655], [7, 42310], [8, 43002], [9, 44118], [10, 25690, true]])
      ]);
    case "future":
      return comparison(planId, "2028-01-01", "2029-12-31", [fullYear(2028, 51000), fullYear(2029, 53000)]);
    case "past":
      return comparison(planId, "2022-01-01", "2023-12-31", [fullYear(2022, 31000), fullYear(2023, 33000)]);
    case "zeros":
      return comparison(planId, "2026-01-01", "2026-12-31", [
        buildYear(2026, Array.from({length: 12}, (_, i) => [i + 1, 0] as MonthTuple))
      ]);
    case "long":
      return comparison(planId, "2024-01-01", "2029-12-31",
        [2024, 2025, 2026, 2027, 2028, 2029].map((year, i) => fullYear(year, 40000 + i * 2000)));
    case "ok":
    default:
      return {...structuredClone(COMPARISON_FIXTURE), planId};
  }
}
